import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useStore } from "ra-core";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";

export interface WorkspaceMembership {
  workspaceId: string;
  workspaceName: string;
  industry: string | null;
  roleKey: string;
  roleName: string;
}

interface MembershipRow {
  workspace_id: string;
  user_id: string;
  roles: { key: string; name: string } | null;
  workspaces: { id: string; name: string; industry: string | null } | null;
}

const ACTIVE_WORKSPACE_STORE_KEY = "gravun.activeWorkspaceId";

/**
 * The current user's workspace memberships, resolved from the real Gravun
 * workspace model (RLS already scopes rows to workspaces the user belongs to).
 *
 * The "active" workspace is client state only, data isolation NEVER depends
 * on it; RLS enforces membership on every query regardless of the selection.
 */
export const useWorkspaces = () => {
  const [activeWorkspaceId, setActiveWorkspaceId] = useStore<string | null>(
    ACTIVE_WORKSPACE_STORE_KEY,
    null,
  );

  const { data: memberships = [], isPending } = useQuery({
    queryKey: ["gravun", "workspace-memberships"],
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<WorkspaceMembership[]> => {
      const supabase = getSupabaseClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("workspace_members")
        .select(
          "workspace_id, user_id, roles(key, name), workspaces(id, name, industry)",
        )
        .eq("user_id", user.id);
      if (error) throw error;

      return ((data ?? []) as unknown as MembershipRow[]).map((row) => ({
        workspaceId: row.workspace_id,
        workspaceName: row.workspaces?.name ?? "Workspace",
        industry: row.workspaces?.industry ?? null,
        roleKey: row.roles?.key ?? "member",
        roleName: row.roles?.name ?? "Member",
      }));
    },
  });

  const activeWorkspace =
    memberships.find((m) => m.workspaceId === activeWorkspaceId) ??
    memberships[0] ??
    null;

  // Persist the resolved default so non-React consumers (the data provider)
  // scope queries to the same workspace the UI displays.
  useEffect(() => {
    if (activeWorkspace && activeWorkspace.workspaceId !== activeWorkspaceId) {
      setActiveWorkspaceId(activeWorkspace.workspaceId);
    }
  }, [activeWorkspace, activeWorkspaceId, setActiveWorkspaceId]);

  return {
    memberships,
    activeWorkspace,
    setActiveWorkspaceId,
    isPending,
  };
};
